import React from 'react';
import { FieldError, UseControllerProps, useController } from 'react-hook-form';
import { FormattedMessage, defineMessages } from 'react-intl';

import * as yup from 'yup';

import { Select, Props as SelectProps } from '../Select/Select';

export const FORM_SELECT_LANGUAGE = 'language';

export type FormFieldsLanguage = {
  [FORM_SELECT_LANGUAGE]: string;
};

export type FormSelectLanguageErrors = 'selectLanguageEmptyError';

export type Props = UseControllerProps<FormFieldsLanguage> & SelectProps & {
  error?: FieldError;
};

export const selectLanguageRequiredSchema = yup.string().required('selectLanguageEmptyError');

export const hooks = {
  useGetHandleChange(
    onChange: (id: string) => void,
    onChangeProp: (id: string) => void,
  ) {
    return React.useCallback((id: string) => {
      onChange(id);
      onChangeProp(id);
    }, [onChange, onChangeProp]);
  },
};

const messages = defineMessages({
  selectLanguage: {
    id: 'src.components.FormSelectLanguage.selectLanguage',
    defaultMessage: 'Select your language',
  },
  selectLanguageEmptyError: {
    id: 'src.components.FormSelectLanguage.selectLanguageEmptyError',
    defaultMessage: 'Language is a required field',
  },
});

export const FormSelectLanguage = React.memo(React.forwardRef((
  props: Props,
  ref: React.RefObject<HTMLDivElement>,
) => {
  const {
    items,
    control,
    name,
    onChange: onChangeProp,
    error,
  } = props;

  const { field: {
    onChange,
    value,
    ...rest
  } } = useController({
    name,
    control,
  });

  const handleChange = hooks.useGetHandleChange(onChange, onChangeProp);

  return (
    <Select
      {...rest}
      onChange={handleChange}
      items={items}
      defaultSelectedItemId={value}
      search={true}
      ref={ref}
      label={<FormattedMessage {...messages.selectLanguage} />}
      labelSize='small'
      errorMessage={
        <FormattedMessage {...messages[error?.message as FormSelectLanguageErrors]} />
      }
    />
  );
}));

FormSelectLanguage.displayName = 'FormSelectLanguage';
